import { AlertTriangle, CheckCircle2, History, Info, ScrollText } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Breadcrumb } from '../../../../components/Breadcrumb';
import { CountBadge } from '../../../../components/CountBadge';
import { ErrorState } from '../../../../components/ErrorState';
import type { FolderTab } from '../../../../components/FolderTabs';
import { HistoryLogsModal } from '../../../../components/index-page/HistoryLogsModal';
import { PreviewTable, type PreviewRow } from '../../../../components/PreviewTable';
import { ProfileBanner, type ProfileBannerTone } from '../../../../components/profile/ProfileBanner';
import { ProfileCard } from '../../../../components/profile/ProfileCard';
import { ProfileHeader } from '../../../../components/profile/ProfileHeader';
import { ProfileHeaderShell } from '../../../../components/profile/ProfileHeaderShell';
import { ShortcutButton } from '../../../../components/ShortcutButton';
import { Skeleton } from '../../../../components/Skeleton';
import { StatusText } from '../../../../components/table/StatusText';
import type { AuditLogDetail } from '../auditLogTypes';
import { EntityChangeList } from '../components/EntityChangeList';
import { useAuditLogEntry } from '../useAuditLogEntry';

type TabKey = 'info' | 'client' | 'changes';

/**
 * The audit-log profile page, on the **header** profile layout: header strip on top, folder
 * tabs over one panel below.
 *
 * An audit entry reads as one continuous thing — one request, one actor, one instant — so it
 * takes the header shape rather than the sidebar one. `Info` is the first tab on every
 * profile: what the record is, plus its audit trail.
 *
 * The detail modal (§13) shows the request-context subset; this page shows every column the
 * pipeline captured — actor, session, network, client, and the field-level changes — because
 * the trail's job is to answer "what exactly happened" without a second query.
 *
 * Reached by clicking a log on the global Audit Logs page (anchor column or the row menu).
 * The breadcrumb is the back-nav, so there is no Back button (§6).
 */
export function AuditLogProfilePage() {
  const { id } = useParams<{ id: string }>();

  // A hand-typed or stale URL must not turn into a request for `/api/audit-logs/NaN`.
  const parsed = Number(id);
  const entryId = Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;

  const { entry, changes, loading, error, reload } = useAuditLogEntry(entryId);

  const [tab, setTab] = useState<TabKey>('info');
  const [historyOpen, setHistoryOpen] = useState(false);

  /*
    Every tab reads what `audit-logs.read` already covers, and the route gate carries that
    key — so none of them declares one of its own. Gating them again would hide tabs from a
    caller who is, by definition, already looking at the record.
  */
  const tabs: FolderTab<TabKey>[] = [
    { key: 'info', label: 'Info' },
    { key: 'client', label: 'Client' },
    {
      key: 'changes',
      label: (
        <>
          Changes
          <CountBadge count={changes.length} tone="slate" />
        </>
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-white">
      <Breadcrumb
        segments={[
          'Administration',
          { label: 'Audit Logs', to: '/audit-logs' },
          entry ? `${entry.action} · #${entry.id}` : `Entry #${id ?? ''}`,
        ]}
      />

      {entryId === null ? (
        <ErrorState message="That is not a valid audit entry reference." />
      ) : loading ? (
        <ProfileSkeleton />
      ) : error ? (
        <ErrorState
          message={error.message}
          correlationId={error.correlationId}
          // A missing entry will still be missing on a retry; anything else may not be.
          onRetry={error.isNotFound ? undefined : reload}
        />
      ) : entry ? (
        <ProfileHeaderShell<TabKey>
          id="audit-log-profile"
          tabs={tabs}
          activeTab={tab}
          onTabChange={setTab}
          header={
            <ProfileHeader
              id="audit-log-profile-header"
              icon={<ScrollText className="h-5 w-5" />}
              title={entry.action}
              // The request line identifies the event and needs no label. Absent on a row
              // written outside a request, where the shell drops the line entirely.
              meta={[entry.httpMethod ? `${entry.httpMethod} ${entry.path ?? ''}`.trim() : null]}
              status={<OutcomePill entry={entry} />}
              createdBy={actioner(entry)}
              createdAt={entry.timestamp}
            />
          }
          toolbar={
            /*
              Record-level, and the only action an append-only entry has: everything else the
              trail recorded about the same record. Scoped to this entry's entity, so it is
              state — not permission — that decides whether it can run, and a disabled chip
              says why.

              No <PermissionGate>: the route already gates on `audit-logs.read`, which is the
              same key this opens.
            */
            <ShortcutButton
              id="audit-log-profile-history"
              size="xs"
              icon={<History className="h-3.5 w-3.5" />}
              abbr="LOG"
              label="History logs for this record"
              tooltip="Every recorded change to the record this entry touched"
              disabledReason={
                entry.entityType
                  ? null
                  : 'This entry touched no record, so there is no history to show.'
              }
              onClick={() => setHistoryOpen(true)}
            />
          }
          banner={<OutcomeBanner entry={entry} />}
        >
          {tab === 'info' ? (
            <>
              <ProfileCard id="audit-log-profile-basic-info" title="Basic Info">
                <PreviewTable variant="table-display" rows={basicInfoRows(entry)} />
              </ProfileCard>

              {/*
                The actioner's display name and the instant are in the header, on screen for
                every tab. This card carries what identifies the account behind that name.
              */}
              <ProfileCard id="audit-log-profile-actor" title="Actor">
                <PreviewTable variant="table-display" rows={actorRows(entry)} />
              </ProfileCard>

              {/* Never editable: the trail records what happened. */}
              <ProfileCard id="audit-log-profile-audit" title="Audit">
                <PreviewTable variant="table-display" rows={auditRows(entry)} />
              </ProfileCard>
            </>
          ) : null}

          {tab === 'client' ? (
            <ProfileCard
              id="audit-log-profile-client"
              title="Client"
              description="Where the request came from. The address is the caller's own only when a declared reverse proxy forwarded it; otherwise it is the peer that connected."
            >
              <PreviewTable variant="table-display" rows={clientRows(entry)} />
            </ProfileCard>
          ) : null}

          {tab === 'changes' ? (
            <ProfileCard
              padded
              id="audit-log-profile-changes"
              title="Entity Changes"
              count={changes.length}
              description="Every entity this request mutated, with the original and new value of each field that moved. Sensitive fields are masked rather than omitted."
            >
              <EntityChangeList changes={changes} />
            </ProfileCard>
          ) : null}
        </ProfileHeaderShell>
      ) : null}

      {entry?.entityType ? (
        <HistoryLogsModal
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          module="audit-logs"
          entityTypes={[entry.entityType]}
          entityId={entry.entityId ?? undefined}
        />
      ) : null}
    </div>
  );
}

/**
 * The actioner, verbatim as captured. An audit row records the label that was in force at
 * the time, not the account's current one.
 *
 * Never the email: that is the Actor card's, and printing it here as a fallback would put
 * the same string in two slots on the same screen.
 */
function actioner(entry: AuditLogDetail): string | null {
  if (entry.userName?.trim()) {
    return entry.userName;
  }

  // No account at all — a failed sign-in, or a request that never authenticated.
  return entry.userId || entry.userEmail ? null : 'anonymous';
}

/** The always-on state indicator, which survives the banner being dismissed. */
function OutcomePill({ entry }: { entry: AuditLogDetail }) {
  if (entry.statusCode === null) {
    return <StatusText tone="inactive">no request</StatusText>;
  }

  return (
    <StatusText tone={entry.succeeded ? 'active' : 'blocked'}>
      {entry.statusCode} {entry.succeeded ? 'ok' : 'failed'}
    </StatusText>
  );
}

/**
 * The record's state in one line. Rendered for every outcome — success included — because a
 * banner that appears only on failure teaches the reader to skip the region.
 *
 * No action: an audit entry is append-only and there is nothing to do to it.
 */
function OutcomeBanner({ entry }: { entry: AuditLogDetail }) {
  const { tone, icon, message } = describeOutcome(entry);

  return <ProfileBanner id="audit-log-profile-banner" tone={tone} icon={icon} message={message} />;
}

/**
 * The outcome in one sentence. It deliberately omits the status code, which the header's
 * pill already carries — the banner says what happened, the pill says what it was.
 */
function describeOutcome(entry: AuditLogDetail): {
  tone: ProfileBannerTone;
  icon: ReactNode;
  message: string;
} {
  if (entry.statusCode === null) {
    return {
      tone: 'slate',
      icon: <Info className="h-4 w-4" aria-hidden="true" />,
      message:
        'Recorded outside an HTTP request — written straight to the trail by a background job or system task, so there is no request outcome to report.',
    };
  }

  if (entry.succeeded) {
    return {
      tone: 'emerald',
      icon: <CheckCircle2 className="h-4 w-4" aria-hidden="true" />,
      message:
        'The request completed. Audit entries are append-only: this record cannot be edited or removed.',
    };
  }

  return {
    tone: 'red',
    icon: <AlertTriangle className="h-4 w-4" aria-hidden="true" />,
    message: entry.errorMessage
      ? `The request failed. ${entry.errorMessage}`
      : 'The request failed, and no reason was captured. Changes shows what, if anything, reached the database.',
  };
}

/** What the request was. The action is the header's title and the outcome is its pill. */
function basicInfoRows(entry: AuditLogDetail): PreviewRow[] {
  return [
    { label: 'Module', value: entry.category },
    {
      label: 'Entity',
      value: entry.entityType
        ? [entry.entityType, entry.entityDisplay].filter(Boolean).join(' · ')
        : null,
    },
    { label: 'Entity ID', value: mono(entry.entityId) },
    /*
      The record this belonged to. On a write to a SECTION of a record the row above is the
      section's own id — a survey answer, a line of service — and this is the employee it was
      filed under, which is the id that answers "whose record did this change".

      Rendered even when it repeats Entity ID, which is what a write to the record itself
      produces: every row of this table renders whatever the value, or a reader cannot tell a
      fact that is missing from one that does not apply.
    */
    { label: 'Parent record ID', value: mono(entry.parentEntityId) },
    { label: 'Duration', value: mono(entry.durationMs != null ? `${entry.durationMs} ms` : null) },
    // Sanitized server-side — sensitive parameter values arrive as `***`.
    { label: 'Query string', value: mono(entry.queryString), full: true },
  ];
}

/** Who acted, beyond the name already in the header. */
function actorRows(entry: AuditLogDetail): PreviewRow[] {
  return [
    { label: 'Email', value: mono(entry.userEmail) },
    { label: 'User ID', value: mono(entry.userId) },
    { label: 'Roles at the time', value: entry.roles?.split(',').join(', ') || null },
    { label: 'Session', value: mono(entry.tokenId) },
  ];
}

/** The trail's own metadata. Creation is in the header, so it is not repeated here. */
function auditRows(entry: AuditLogDetail): PreviewRow[] {
  return [
    { label: 'Entry ID', value: mono(String(entry.id)) },
    { label: 'Correlation ID', value: mono(entry.correlationId) },
    // Stated rather than left as an em-dash: "never updated" is a fact about the trail, not
    // missing data.
    { label: 'Updated', value: 'Never — the audit trail is append-only' },
  ];
}

/** Where the request came from. */
function clientRows(entry: AuditLogDetail): PreviewRow[] {
  return [
    { label: 'IP address', value: mono(entry.ipAddress) },
    { label: 'Forwarded chain', value: mono(entry.forwardedFor), full: true },
    { label: 'User agent', value: mono(entry.userAgent), full: true },
    { label: 'Origin', value: mono(entry.origin), full: true },
    { label: 'Referer', value: mono(entry.referer), full: true },
  ];
}

/**
 * Monospaces a machine value, and collapses an absent one to `null` so `<PreviewTable>`
 * renders its em-dash instead of an empty mono span.
 */
function mono(value: string | null | undefined): ReactNode {
  return value ? <span className="font-mono tabular-nums">{value}</span> : null;
}

/** Mirrors the loaded layout so nothing shifts when the entry lands. */
function ProfileSkeleton() {
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      <div className="flex shrink-0 items-start gap-4 border-b border-slate-200 px-4 py-3.5">
        <Skeleton className="h-11 w-11 rounded-xl" />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Skeleton className="h-4 w-48" />
          <Skeleton className="h-3 w-64" />
        </div>
        <Skeleton className="h-4 w-24" />
      </div>

      <div className="shrink-0 border-b border-slate-200 px-4 py-2.5">
        <Skeleton className="h-14 w-14 rounded-panel" />
      </div>

      <div className="shrink-0 border-b border-slate-200 px-4 py-3">
        <Skeleton className="h-12 w-full rounded-xl" />
      </div>

      <div className="shrink-0 bg-slate-50/60 px-4 pt-2">
        <div className="flex gap-1">
          <Skeleton className="h-9 w-20 rounded-t-lg" />
          <Skeleton className="h-9 w-24 rounded-t-lg" />
          <Skeleton className="h-9 w-28 rounded-t-lg" />
        </div>
      </div>

      <div className="min-h-0 flex-1 border-t border-slate-200">
        <div className="mx-auto flex min-w-0 max-w-4xl flex-col gap-5 p-5">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  );
}
